import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { SubmitButton } from '../../../../components/SubmitButton/SubmitButton'
import { itemQueryKey, itemsQueryKey } from '../../../../api/queries'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutationCreateItem, mutationEditItem } from '../../../../api/mutations'
import { ErrorExplanation } from '../../../../components/ErrorExplanation/ErrorExplanation'
import { DialogActions, DialogContent } from '../../../../components/Modal/Modal'
import { FormRow } from '../../../../components/FormRow/FormRow'
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { useApiErrors } from '../../../../hooks/useApiErrors'
import {
  EquipSlotTranslation,
  IEquipSlotTypes,
  IItem,
  IItemQualities,
  ItemQualityTranslation,
  IWeaponProfTypes,
  WeaponProfTranslation,
} from '../../../../api/types/item'
import { EffectFormAttributes } from '../../../../components/EffectFormAttributes/EffectFormAttributes'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      margin: theme.spacing(0.5),
    },
  })
)

interface Props {
  item?: IItem
  afterSuccess?: () => void
}

export const ItemForm: React.FC<Props> = ({ item, afterSuccess }) => {
  const { apiErrors, setApiErrors, setApiErrorsFromAxios } = useApiErrors()
  const { register, handleSubmit, errors, control } = useForm<IItem>()
  const queryClient = useQueryClient()
  const [quality, setQuality] = useState(item?.quality || 'common')

  const { mutate: executeMutation, isLoading: isMutating } = useMutation(
    (item: IItem) => (item.id ? mutationEditItem(item.id, item) : mutationCreateItem(item)),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(
          err,
          `Nie udało się ${item?.id ? 'zmodyfikować' : 'stworzyć'} przedmiotu`
        )
      },
      onSuccess: async () => {
        setApiErrors([])
        if (item?.id) {
          queryClient.invalidateQueries(itemQueryKey(item.id)).then(afterSuccess)
        } else {
          queryClient.invalidateQueries(itemsQueryKey()).then(afterSuccess)
        }
      },
    }
  )
  const onSubmit = (data: IItem) => executeMutation(data)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {item?.id && <input type="hidden" name="id" value={item?.id} ref={register()} />}

        <DialogContent dividers>
          <Box sx={{ marginBottom: '8px' }}>
            <Typography variant="button" display="block" gutterBottom>
              Podstawowe atrybuty
            </Typography>

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <Controller
                control={control}
                name="quality"
                defaultValue={item?.quality}
                render={({ onChange, value, name, ref }) => (
                  <TextField
                    sx={{ marginRight: '8px' }}
                    select
                    fullWidth={true}
                    name="quality"
                    label="Jakość"
                    variant="outlined"
                    inputRef={ref}
                    onChange={val => {
                      onChange(val)
                      setQuality(val.target.value)
                    }}
                    defaultValue={item?.quality || 'common'}
                  >
                    {IItemQualities.map(q => (
                      <MenuItem key={q} value={q}>
                        {ItemQualityTranslation[q]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>
            <Box sx={{ marginBottom: '8px' }}>
              <TextField
                name="name"
                label="Nazwa"
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.name}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.name}
                helperText={errors.name && errors.name.message}
              />
            </Box>
            <Box sx={{ marginBottom: '8px' }}>
              <TextField
                multiline
                name="description"
                label="Opis"
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.description}
                rows={4}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.description}
                helperText={errors.description && errors.description.message}
              />
            </Box>

            {quality !== 'common' && (
              <>
                <Box sx={{ marginBottom: '8px' }}>
                  <TextField
                    name="name_unidentified"
                    label="Nazwa niezidentyfikowanego"
                    fullWidth={true}
                    variant="outlined"
                    defaultValue={item?.name_unidentified}
                    inputProps={{
                      ref: register(),
                    }}
                    error={!!errors.name_unidentified}
                    helperText={errors.name_unidentified && errors.name_unidentified.message}
                  />
                </Box>

                <Box sx={{ marginBottom: '8px' }}>
                  <TextField
                    multiline
                    name="description_unidentified"
                    label="Opis niezidentyfikowanego"
                    fullWidth={true}
                    variant="outlined"
                    defaultValue={item?.description_unidentified}
                    rows={4}
                    inputProps={{
                      ref: register(),
                    }}
                    error={!!errors.description_unidentified}
                    helperText={
                      errors.description_unidentified && errors.description_unidentified.message
                    }
                  />
                </Box>
              </>
            )}

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                sx={{ marginRight: '8px' }}
                name="charges"
                label="Ładunki"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.charges}
                inputProps={{
                  ref: register(),
                }}
              />
              <TextField
                name="weight"
                label="Waga (kg)"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.weight || 0.0}
                inputProps={{
                  ref: register(),
                  step: 0.1,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                sx={{ marginRight: '8px' }}
                name="delay"
                label="Opóźnienie"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.delay}
                inputProps={{
                  ref: register(),
                  step: 0.5,
                }}
              />
              <TextField
                sx={{ marginRight: '8px' }}
                name="range"
                label="Zasięg"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.range}
                inputProps={{
                  ref: register(),
                }}
              />
              <TextField
                name="strength_mod"
                label="Użycie siły"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={item?.strength_mod}
                inputProps={{
                  ref: register(),
                  step: 0.5,
                  min: 0,
                  max: 9,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <Controller
                control={control}
                name="weapon_prof"
                defaultValue={item?.weapon_prof}
                render={({ onChange, value, name, ref }) => (
                  <TextField
                    sx={{ marginRight: '8px' }}
                    select
                    fullWidth={true}
                    name="weapon_prof"
                    label="Biegłość broni"
                    variant="outlined"
                    inputRef={ref}
                    onChange={val => onChange(val)}
                    defaultValue={item?.weapon_prof}
                  >
                    <MenuItem value={''}>Brak</MenuItem>
                    {IWeaponProfTypes.map(wp => (
                      <MenuItem key={wp} value={wp}>
                        {WeaponProfTranslation[wp]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name="equip_slot"
                defaultValue={item?.equip_slot}
                render={({ onChange, value, name, ref }) => (
                  <TextField
                    select
                    fullWidth={true}
                    name="equip_slot"
                    label="Slot ekwipunku"
                    variant="outlined"
                    inputRef={ref}
                    onChange={val => onChange(val)}
                    defaultValue={item?.equip_slot}
                  >
                    <MenuItem value={''}>Brak</MenuItem>
                    {IEquipSlotTypes.map(slot => (
                      <MenuItem key={slot} value={slot}>
                        {EquipSlotTranslation[slot]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <FormControlLabel
                sx={{ marginLeft: '0px' }}
                control={<Checkbox color="primary" defaultChecked={item?.two_handed} />}
                label="Dwuręczny"
                labelPlacement="end"
                name="two_handed"
                inputRef={register}
              />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <FormControlLabel
                control={<Checkbox color="primary" defaultChecked={item?.consumable} />}
                label="Konsumowalny"
                labelPlacement="end"
                name="consumable"
                inputRef={register}
              />
              <FormControlLabel
                control={<Checkbox color="primary" defaultChecked={item?.stackable} />}
                label="Stackowalny"
                labelPlacement="end"
                name="stackable"
                inputRef={register}
              />
              <FormControlLabel
                control={<Checkbox color="primary" defaultChecked={item?.target_self} />}
                label="Przedmiotu można użyć tylko na sobie"
                labelPlacement="end"
                name="target_self"
                inputRef={register}
              />
            </Box>
          </Box>

          <EffectFormAttributes effect={item?.effect} register={register} errors={errors} />

          <FormRow>
            <ErrorExplanation errors={apiErrors} />
          </FormRow>
        </DialogContent>
        <DialogActions>
          <SubmitButton disabled={isMutating}>{item?.id ? 'Aktualizuj' : 'Stwórz'}</SubmitButton>
        </DialogActions>
      </form>
    </>
  )
}
